import { motion } from "framer-motion";
import { ArrowRight, Banknote, Factory, GraduationCap, Award } from "lucide-react";
import { KineticHeadline, ScrollReveal } from "@/components/Kinetic";

const CHAPTERS = [
  {
    num: "01",
    icon: GraduationCap,
    title: "Year 1 — Foundation",
    desc: "Theory + intensive workshop training at our 25,000 sq ft Gurugram campus.",
  },
  {
    num: "02",
    icon: Factory,
    title: "Year 2 onwards — On-Job Training",
    desc: "Train inside MNC companies from America, India, Germany, Japan & Switzerland such as Boston Scientific, IFB, Subros, Ensto, Giesecke + Devrient, Bucher etc.",
  },
  {
    num: "03",
    icon: Banknote,
    title: "Monthly Stipend",
    desc: "Receive a training allowance from the partner company every month.",
  },
  {
    num: "04",
    icon: Award,
    title: "HWK-Koblenz Certified Diploma",
    desc: "Placement opportunity in India & abroad after completion of 3.5 years.",
  },
];

export default function EarnSection() {
  return (
    <section
      id="earn"
      data-testid="earn-section"
      aria-label="Earn while you learn — work-study program"
      className="py-16 sm:py-24 lg:py-32 bg-yellow-400 relative overflow-hidden grain"
    >
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute -top-24 -right-24 h-72 w-72 bg-slate-950/10 rounded-full blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Left column */}
          <div className="md:col-span-5 md:sticky md:top-24">
            <ScrollReveal>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-900/70">
                The Kiki Manifesto · Chapter II
              </div>
            </ScrollReveal>

            <KineticHeadline
              as="h2"
              delay={0.05}
              stagger={0.09}
              className="font-display mt-3 text-3xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-950 leading-[1.02] sm:leading-[0.95]"
              lines={[
                <>First time in India.</>,
                <span className="underline decoration-slate-950 decoration-[4px] sm:decoration-[6px] underline-offset-4">
                  Earn while you learn.
                </span>,
              ]}
            />

            <ScrollReveal delay={0.2}>
              <p className="mt-4 sm:mt-5 text-base sm:text-lg text-slate-900/80 leading-relaxed max-w-md">
                Modelled after Germany's famous "Berufsschule" work-study system —
                one of the most successful in the world. Students don't just learn
                theoretical & practical skills, they also earn money while
                building a real career.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.35}>
              <motion.a
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href="#apply"
                data-testid="earn-cta-button"
                className="mt-7 sm:mt-8 inline-flex items-center gap-2 bg-slate-950 text-white px-6 sm:px-7 h-12 sm:h-14 py-3 sm:py-4 font-bold uppercase tracking-wider text-sm sm:text-base hover:bg-slate-800 transition-colors group shine-on-hover"
              >
                Reserve My Seat
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
            </ScrollReveal>
          </div>

          {/* Right column — Numbered manifesto chapters */}
          <div className="md:col-span-7 relative">
            <div className="absolute left-8 top-0 bottom-0 w-px bg-slate-950/20 hidden sm:block" aria-hidden />

            <ol className="space-y-4 sm:space-y-6">
              {CHAPTERS.map((c, i) => {
                const Icon = c.icon;
                return (
                  <motion.li
                    key={c.num}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    className="relative bg-white border-2 border-slate-950 p-5 sm:p-7 sm:pl-24 group hover:-translate-y-1 transition-transform duration-500 ease-out"
                  >
                    {/* Chapter number */}
                    <div className="sm:absolute sm:left-0 sm:top-1/2 sm:-translate-y-1/2 sm:-translate-x-4 h-14 w-14 sm:h-16 sm:w-16 bg-slate-950 text-yellow-400 grid place-items-center border-2 border-slate-950 font-display font-black text-xl sm:text-2xl mb-3 sm:mb-0 relative z-10">
                      {c.num}
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className="h-5 w-5 text-slate-950" strokeWidth={2.25} />
                          <h3 className="font-display text-lg sm:text-xl font-bold text-slate-950 tracking-tight">
                            {c.title}
                          </h3>
                        </div>
                        <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
                          {c.desc}
                        </p>
                      </div>
                    </div>

                    {/* Yellow marker on hover */}
                    <motion.span
                      className="absolute left-0 top-0 h-full w-[3px] bg-slate-950"
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      viewport={{ once: true, amount: 0.5 }}
                      transition={{ duration: 0.6, delay: i * 0.08 + 0.3 }}
                      style={{ transformOrigin: "top" }}
                    />
                  </motion.li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
